import type { FC, ReactNode } from 'react'
import type { NavLink } from '@/types'

import { ArrowRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button-link'
import { Container } from '@/components/generals/container'
import { WindowFrame } from '@/components/generals/window-frame'

interface CtaSectionProps {
    eyebrow?: string
    title?: string
    description?: string
    primary?: NavLink
    secondary?: NavLink
}

const NEXT_STEPS = [
    { label: "Choose a course", done: true },
    { label: "Pick a class time", done: false },
    { label: "Meet your instructor", done: false },
    { label: "Earn your certificate", done: false }
]

export const CtaSection: FC<CtaSectionProps> = ({
    eyebrow = "Ready when you are",
    title = "Your next skill starts with one class.",
    description = "Tell us which course interests you and when you can study. An advisor will reply within one working day to help you choose the right batch.",
    primary = { label: "Enroll now", href: '/enroll' },
    secondary = { label: "Browse courses", href: '/courses' }
}): ReactNode => {
    return (
        <section id='enroll-cta' aria-labelledby='enroll-cta-title' className='py-20 sm:py-24'>
            <Container>
                <div className='relative overflow-hidden rounded-[2rem] bg-primary px-6 py-14 text-primary-foreground sm:px-12 sm:py-16 lg:px-16'>
                    <div
                        aria-hidden='true'
                        className='bg-dots pointer-events-none absolute inset-0 text-primary-foreground/40 [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]'
                    />
                    <div aria-hidden='true' className='pointer-events-none absolute -bottom-32 -left-24 size-80 rounded-full bg-accent/30 blur-3xl' />

                    <div className='relative grid items-center gap-12 lg:grid-cols-[1.35fr_1fr]'>
                        <div>
                            <p className='inline-flex items-center gap-2 font-mono text-xs font-medium tracking-wider text-accent uppercase'>
                                <span aria-hidden='true'>{'//'}</span>
                                {eyebrow}
                            </p>

                            <h2 id='enroll-cta-title' className='mt-3 text-3xl leading-[1.08] font-semibold sm:text-5xl'>
                                {title}
                            </h2>

                            <p className='mt-5 max-w-xl text-base leading-relaxed text-primary-foreground/80 sm:text-lg'>
                                {description}
                            </p>

                            <div className='mt-9 flex flex-col gap-3 sm:flex-row'>
                                <ButtonLink href={primary.href} variant='accent' size='lg'>
                                    {primary.label}
                                    <ArrowRight aria-hidden='true' />
                                </ButtonLink>

                                <ButtonLink href={secondary.href} variant='outline-light' size='lg'>
                                    {secondary.label}
                                </ButtonLink>
                            </div>
                        </div>

                        <div aria-hidden='true' className='hidden lg:block'>
                            <WindowFrame tone='dark' title='next-steps.txt' className='rotate-2'>
                                <ul className='space-y-3 p-6 font-mono text-sm'>
                                    {NEXT_STEPS.map(step => (
                                        <li key={step.label} className='flex items-center gap-3'>
                                            <span
                                                className={cn(
                                                    'flex size-5 items-center justify-center rounded border',
                                                    step.done ? 'border-accent bg-accent text-accent-foreground' : 'border-background/30'
                                                )}
                                            >
                                                {step.done && <Check className='size-3.5' />}
                                            </span>

                                            <span className={step.done ? 'text-background/50 line-through' : 'text-background'}>
                                                {step.label}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </WindowFrame>
                        </div>
                    </div>
                </div>
            </Container>
        </section>
    )
}
